// ── API types ──────────────────────────────────────────────────────────────────

import { env } from "@/env";

export interface DasamsaRequest {
  name: string;
  birth_date: string;
  birth_time?: string;
  birth_place: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

export interface D10PlanetPosition {
  planet_id: string;
  planet_display_en: string;
  planet_display_hi: string;
  natal_longitude: number;
  d10_sign: string;
  d10_sign_index: number;
  d10_house: number;
}

export interface PlanetD10Strength {
  planet_id: string;
  planet_display_en: string;
  is_exalted_d10: boolean;
  is_debilitated_d10: boolean;
  is_own_sign_d10: boolean;
  career_strength: "strong" | "average" | "weak";
  karaka_domain: string;
}

export interface CareerIndicator {
  indicator: string;
  description: string;
}

export interface DasamsaResponse {
  system: string;
  name: string;
  birth_date: string;
  d10_lagna: string;
  d10_lagna_index: number;
  d10_chart: D10PlanetPosition[];
  career_indicators: CareerIndicator[];
  planet_d10_strength: PlanetD10Strength[];
  predictions: string[];
}

// ── API call ───────────────────────────────────────────────────────────────────

// NEXT_PUBLIC_API_URL already includes the `/api/v1` suffix (see env.ts), so
// paths here must NOT re-prepend it. The previous `${API_BASE}/api/v1/...`
// produced `/api/v1/api/v1/dasamsa-chart` (404) and the `api.ishvaram.com`
// fallback host does not resolve — both silently broke this SEO page's data.
const API_BASE = env.NEXT_PUBLIC_API_URL;

export async function fetchDasamsaChart(req: DasamsaRequest): Promise<DasamsaResponse> {
  const res = await fetch(`${API_BASE}/dasamsa-chart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail ?? "Chart computation failed");
  }
  return res.json() as Promise<DasamsaResponse>;
}

// ── Shared constants ───────────────────────────────────────────────────────────

export const STRENGTH_COLOR: Record<string, string> = {
  strong: "var(--ish-success, #16a34a)",
  average: "var(--ish-warning, #ca8a04)",
  weak: "var(--ish-error, #dc2626)",
};
